import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useMemo } from 'react'
import { useCart } from '../../cart'
import { buildCheckoutSavedAddresses } from '../../checkout/addressBookAdapter'
import { useCheckoutFlow } from '../../checkout/flow'
import {
  CheckoutOrderSummary,
  CheckoutProgress,
} from '../../checkout/steps'
import { useCustomerAuth } from '../../customer-auth'
import { useShopI18n } from '../../i18n'
import { CheckoutEmptyState, CheckoutErrorBanner, CheckoutStepContent } from './checkout-page'

const CheckoutPage = () => {
  const prefersReducedMotion = Boolean(useReducedMotion())
  const { token, authenticated, initializing: authInitializing, profile } = useCustomerAuth()
  const { cartId, initializing: cartInitializing, resetAfterCheckout } = useCart()
  const { toLocalizedPath, formatCurrency, t } = useShopI18n()

  const { viewModel } = useCheckoutFlow({
    token,
    authenticated,
    authInitializing,
    cartId,
    cartInitializing,
    toLocalizedPath,
    formatCurrency,
    t,
    onCheckoutCompleted: resetAfterCheckout,
  })

  const savedAddresses = useMemo(() => buildCheckoutSavedAddresses(profile), [profile])
  const shippingAddresses = useMemo(
    () => savedAddresses.filter((entry) => entry.useShipping || entry.defaultShipping),
    [savedAddresses],
  )
  const billingAddresses = useMemo(
    () => savedAddresses.filter((entry) => entry.useBilling || entry.defaultBilling),
    [savedAddresses],
  )

  const showMobileStickyCta = viewModel.step === 'review'
  const showEmptyCheckout = viewModel.isEmptyCartState

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_15%_8%,#e0f2ff,transparent_34%),radial-gradient(circle_at_88%_6%,#dcfce7,transparent_32%),linear-gradient(180deg,#f8fafc_0%,#edf2f7_60%,#e2e8f0_100%)]" />
      <div className="shop-layout space-y-5 py-6 pb-24 md:py-8 md:pb-10">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t('checkout.title')}</h1>
          <p className="text-sm text-slate-600">{t('checkout.subtitle')}</p>
        </header>

        {showEmptyCheckout ? null : (
          <CheckoutProgress steps={viewModel.progressSteps} currentOrder={viewModel.currentProgressOrder} />
        )}

        {!showEmptyCheckout ? (
          <CheckoutErrorBanner viewModel={viewModel} t={t} toLocalizedPath={toLocalizedPath} />
        ) : null}

        {showEmptyCheckout ? (
          <CheckoutEmptyState t={t} toLocalizedPath={toLocalizedPath} />
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
            <div>
              {viewModel.isBusy && !viewModel.session ? (
                <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                  {t('checkout.loadingSession')}
                </div>
              ) : null}

              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={viewModel.step}
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
                  animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -12 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.18, ease: 'easeOut' }}
                >
                  <CheckoutStepContent
                    viewModel={viewModel}
                    shippingAddresses={shippingAddresses}
                    billingAddresses={billingAddresses}
                    formatCurrency={formatCurrency}
                    t={t}
                    toLocalizedPath={toLocalizedPath}
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <CheckoutOrderSummary
              items={viewModel.cartItems}
              totalsRows={viewModel.totalsRows}
              sessionStatus={viewModel.session?.status ?? null}
              isSubmitting={viewModel.isBusy}
              formatCurrency={formatCurrency}
              onBackToCart={() => {
                window.location.assign(toLocalizedPath('/cart'))
              }}
            />
          </div>
        )}
      </div>

      {showMobileStickyCta && !showEmptyCheckout ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden">
          <button
            type="button"
            className="w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={viewModel.isBusy || viewModel.isRedirecting}
            onClick={() => {
              void viewModel.onConfirmCheckout()
            }}
          >
            {viewModel.selectedPaymentProviderRequiresRedirect
              ? t('checkout.cta.proceedToPayment')
              : t('checkout.cta.confirmBankTransferOrder')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default CheckoutPage
